import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import type { RuleStrictness, Rules } from '@/api/teamCategories';
import type { Location } from '@/api/locations';
import { cn } from '@/lib/utils';

// Shared rule-editing form used by both the category rules modal (no
// `inherited` prop) and the team override modal (with `inherited` =
// effective category rules to render as "currently inheriting X" hints).
//
// All sub-components live at module scope so each rule row keeps a stable
// React identity across keystrokes. An earlier version defined them inside
// RulesForm, which made every parent render create new function references
// — React treated each row as a fresh component, unmounted the input, and
// the user lost focus after typing a single character.

export interface RulesFormProps {
  rules: Rules;
  onChange: (next: Rules) => void;

  inherited?: Rules | undefined;
  inheritedLabel?: string;

  locations: Location[];
  selectedLocationIDs: Set<string>;
  onSelectedLocationsChange: (ids: Set<string>) => void;

  selectPortalContainer?: HTMLElement | null;

  disabled?: boolean;
}

const STRICTNESS_VALUES: RuleStrictness[] = ['must', 'target', 'prefer'];

// Helpers that don't need to be re-created on every render.
const numberValueToString = (n: number | null): string =>
  n === null ? '' : String(n);
const parseNumberInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

// ── Sub-component props ─────────────────────────────────────────────
// Shared props passed to every row. Moving these to a stable bundle lets
// the row components stay outside RulesForm without 8-parameter signatures.
interface CommonRowContext {
  rules: Rules;
  inherited?: Rules | undefined;
  inheritedLabel?: string | undefined;
  onUpdate: (patch: Partial<Rules>) => void;
  disabled?: boolean | undefined;
  selectPortalContainer?: HTMLElement | null | undefined;
}

// ── StrictnessSelect ────────────────────────────────────────────────
function StrictnessSelect(props: {
  value: RuleStrictness;
  onChangeValue: (v: RuleStrictness) => void;
  ariaLabel: string;
  disabled?: boolean | undefined;
  selectPortalContainer?: HTMLElement | null | undefined;
}) {
  const { t } = useTranslation('common');
  return (
    <Select
      value={props.value === '' ? '__unset__' : props.value}
      onValueChange={(v) =>
        props.onChangeValue(v === '__unset__' ? '' : (v as RuleStrictness))
      }
      disabled={props.disabled ?? false}
    >
      {/* w-auto overrides the base SelectTrigger's w-full so the trigger
          sizes to its content and the label next to it gets its full line
          back. */}
      <SelectTrigger
        className="w-auto min-w-0 shrink-0 px-2.5"
        aria-label={props.ariaLabel}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent container={props.selectPortalContainer ?? null}>
        <SelectItem value="__unset__">{t('rules.strictness.unset')}</SelectItem>
        {STRICTNESS_VALUES.map((s) => (
          <SelectItem key={s} value={s}>
            {t(`rules.strictness.${s}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── InheritedHint ──────────────────────────────────────────────────
function InheritedHint(props: {
  inherited?: Rules | undefined;
  inheritedLabel?: string | undefined;
  value: string | null | undefined;
}) {
  const { t } = useTranslation('common');
  if (!props.inherited) return null;
  if (props.value === null || props.value === undefined || props.value === '') return null;
  return (
    <span className="text-xs text-muted-foreground">
      {t('rules.inheritedHint', {
        label: props.inheritedLabel ?? t('rules.inheritedDefault'),
        value: props.value,
      })}
    </span>
  );
}

// ── NumberRow ──────────────────────────────────────────────────────
function NumberRow(
  props: CommonRowContext & {
    label: string;
    help?: string;
    field: keyof Rules;
    strictnessField: keyof Rules;
    min?: number;
    max?: number;
    suffix?: string;
  },
) {
  const { t } = useTranslation('common');
  const v = props.rules[props.field] as number | null;
  const inh = props.inherited?.[props.field] as number | null | undefined;
  const strict = props.rules[props.strictnessField] as RuleStrictness;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <label className="text-sm font-medium text-foreground">
          {props.label}
        </label>
        <StrictnessSelect
          value={strict}
          onChangeValue={(s) =>
            props.onUpdate({ [props.strictnessField]: s } as Partial<Rules>)
          }
          ariaLabel={t('rules.strictnessFor', { name: props.label })}
          disabled={props.disabled}
          selectPortalContainer={props.selectPortalContainer}
        />
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          value={numberValueToString(v)}
          onChange={(e) =>
            props.onUpdate({
              [props.field]: parseNumberInput(e.target.value),
            } as Partial<Rules>)
          }
          {...(props.min !== undefined ? { min: props.min } : {})}
          {...(props.max !== undefined ? { max: props.max } : {})}
          disabled={props.disabled ?? false}
          className="w-32"
          placeholder={inh !== null && inh !== undefined ? String(inh) : ''}
        />
        {props.suffix && (
          <span className="text-sm text-muted-foreground">{props.suffix}</span>
        )}
      </div>
      {props.help && (
        <span className="text-xs text-muted-foreground">{props.help}</span>
      )}
      <InheritedHint
        inherited={props.inherited}
        inheritedLabel={props.inheritedLabel}
        value={inh !== null && inh !== undefined ? String(inh) : null}
      />
    </div>
  );
}

// ── EnvelopeRow (target / min / max trio) ──────────────────────────
function EnvelopeRow(
  props: CommonRowContext & {
    label: string;
    help?: string;
    targetField: keyof Rules;
    minField: keyof Rules;
    maxField: keyof Rules;
    strictnessField: keyof Rules;
    suffix?: string;
  },
) {
  const { t } = useTranslation('common');
  const get = (f: keyof Rules) => props.rules[f] as number | null;
  const inh = (f: keyof Rules) =>
    props.inherited?.[f] as number | null | undefined;
  const setV = (f: keyof Rules, raw: string) =>
    props.onUpdate({ [f]: parseNumberInput(raw) } as Partial<Rules>);
  const strict = props.rules[props.strictnessField] as RuleStrictness;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <label className="text-sm font-medium text-foreground">
          {props.label}
        </label>
        <StrictnessSelect
          value={strict}
          onChangeValue={(s) =>
            props.onUpdate({ [props.strictnessField]: s } as Partial<Rules>)
          }
          ariaLabel={t('rules.strictnessFor', { name: props.label })}
          disabled={props.disabled}
          selectPortalContainer={props.selectPortalContainer}
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { lbl: t('rules.envelope.target'), f: props.targetField },
          { lbl: t('rules.envelope.min'), f: props.minField },
          { lbl: t('rules.envelope.max'), f: props.maxField },
        ].map(({ lbl, f }) => (
          <label
            key={String(f)}
            className="flex flex-col gap-1 text-xs text-muted-foreground"
          >
            {lbl}
            <Input
              type="number"
              inputMode="numeric"
              value={numberValueToString(get(f))}
              onChange={(e) => setV(f, e.target.value)}
              disabled={props.disabled ?? false}
              placeholder={
                inh(f) !== null && inh(f) !== undefined ? String(inh(f)) : ''
              }
            />
          </label>
        ))}
      </div>
      {props.suffix && (
        <span className="text-xs text-muted-foreground">{props.suffix}</span>
      )}
      {props.help && (
        <span className="text-xs text-muted-foreground">{props.help}</span>
      )}
    </div>
  );
}

// ── BooleanRow (tri-state pill group) ──────────────────────────────
function BooleanRow(
  props: CommonRowContext & {
    label: string;
    help?: string;
    field: keyof Rules;
    strictnessField: keyof Rules;
  },
) {
  const { t } = useTranslation('common');
  const v = props.rules[props.field] as boolean | null;
  const inh = props.inherited?.[props.field] as boolean | null | undefined;
  const strict = props.rules[props.strictnessField] as RuleStrictness;
  const set = (next: boolean | null) =>
    props.onUpdate({ [props.field]: next } as Partial<Rules>);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <label className="text-sm font-medium text-foreground">
          {props.label}
        </label>
        <StrictnessSelect
          value={strict}
          onChangeValue={(s) =>
            props.onUpdate({ [props.strictnessField]: s } as Partial<Rules>)
          }
          ariaLabel={t('rules.strictnessFor', { name: props.label })}
          disabled={props.disabled}
          selectPortalContainer={props.selectPortalContainer}
        />
      </div>
      <div className="flex gap-1.5">
        {[
          { lbl: t('rules.boolean.unset'), val: null as boolean | null },
          { lbl: t('rules.boolean.yes'), val: true as boolean | null },
          { lbl: t('rules.boolean.no'), val: false as boolean | null },
        ].map(({ lbl, val }) => {
          const active = v === val;
          return (
            <button
              key={String(val)}
              type="button"
              aria-pressed={active}
              disabled={props.disabled ?? false}
              onClick={() => set(val)}
              className={cn(
                'rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
                active
                  ? 'border-lime bg-lime text-ink'
                  : 'border-border bg-background text-muted-foreground hover:bg-muted',
                props.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {lbl}
            </button>
          );
        })}
      </div>
      {props.help && (
        <span className="text-xs text-muted-foreground">{props.help}</span>
      )}
      <InheritedHint
        inherited={props.inherited}
        inheritedLabel={props.inheritedLabel}
        value={
          inh === null || inh === undefined
            ? null
            : inh
              ? t('rules.boolean.yes')
              : t('rules.boolean.no')
        }
      />
    </div>
  );
}

// ── TimePicker (hours + minutes via two Selects) ───────────────────
// Locale-independent picker — two Radix dropdowns with HH (00-23) and MM
// (00, 05, 10, …, 55). Stores "HH:MM" or empty string when both halves
// are unset. The five-minute granularity covers training scheduling
// without flooding the dropdown with 60 options.
const HOURS_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0'),
);
const MINUTES_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

function TimePicker(props: {
  value: string; // "HH:MM" or ""
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  selectPortalContainer?: HTMLElement | null | undefined;
}) {
  const { t } = useTranslation('common');
  const parts = props.value ? props.value.split(':') : ['', ''];
  const hh = parts[0] ?? '';
  const mm = parts[1] ?? '';

  const setHours = (h: string) => {
    if (h === '__unset__') {
      // Clear both halves; semantically "no time set".
      props.onChange('');
      return;
    }
    props.onChange(`${h}:${mm || '00'}`);
  };
  const setMinutes = (m: string) => {
    if (m === '__unset__') {
      props.onChange('');
      return;
    }
    props.onChange(`${hh || '00'}:${m}`);
  };

  return (
    <div className="flex items-center gap-1">
      <Select
        value={hh === '' ? '__unset__' : hh}
        onValueChange={setHours}
        disabled={props.disabled ?? false}
      >
        <SelectTrigger
          className="w-auto min-w-0 px-2.5"
          aria-label={t('rules.timePicker.hourFor', { name: props.ariaLabel })}
        >
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent container={props.selectPortalContainer ?? null}>
          <SelectItem value="__unset__">--</SelectItem>
          {HOURS_OPTIONS.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-sm text-muted-foreground">:</span>
      <Select
        value={mm === '' ? '__unset__' : mm}
        onValueChange={setMinutes}
        disabled={props.disabled ?? false}
      >
        <SelectTrigger
          className="w-auto min-w-0 px-2.5"
          aria-label={t('rules.timePicker.minuteFor', { name: props.ariaLabel })}
        >
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent container={props.selectPortalContainer ?? null}>
          <SelectItem value="__unset__">--</SelectItem>
          {MINUTES_OPTIONS.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── LocationFractionRow ────────────────────────────────────────────
// Two pill rows (preferred / minimum) with the same fixed option set:
// 1, 1/2, 1/4, 1/8. Stored as the denominator (1, 2, 4, 8). null = "no
// preference". One strictness select governs the envelope, same shape as
// EnvelopeRow.
const LOCATION_FRACTION_OPTIONS: { label: string; value: number | null }[] = [
  // null = "no preference" — first option so users default to it.
  { label: '–', value: null },
  { label: '1', value: 1 },
  { label: '1/2', value: 2 },
  { label: '1/4', value: 4 },
  { label: '1/8', value: 8 },
];

function LocationFractionRow(props: CommonRowContext) {
  const { t } = useTranslation('common');
  const preferred = props.rules.preferred_location_fraction_denominator;
  const min = props.rules.min_location_fraction_denominator;
  const strict = props.rules.location_fraction_strictness;

  const renderPills = (
    current: number | null,
    onPick: (v: number | null) => void,
  ) => (
    <div className="flex flex-wrap gap-1.5">
      {LOCATION_FRACTION_OPTIONS.map((opt) => {
        const active = current === opt.value;
        // When changing "preferred" to a smaller share (larger denominator),
        // an existing "min" smaller than preferred would violate the envelope
        // — clear it. The CHECK on the server would also reject, but clearing
        // client-side keeps the UI consistent.
        return (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={active}
            disabled={props.disabled ?? false}
            onClick={() => onPick(opt.value)}
            className={cn(
              'rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
              active
                ? 'border-lime bg-lime text-ink'
                : 'border-border bg-background text-muted-foreground hover:bg-muted',
              props.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <label className="text-sm font-medium text-foreground">
          {t('rules.locationFraction.label')}
        </label>
        <StrictnessSelect
          value={strict}
          onChangeValue={(s) =>
            props.onUpdate({ location_fraction_strictness: s })
          }
          ariaLabel={t('rules.strictnessFor', { name: t('rules.locationFraction.label') })}
          disabled={props.disabled}
          selectPortalContainer={props.selectPortalContainer}
        />
      </div>
      <span className="text-xs text-muted-foreground">
        {t('rules.locationFraction.help')}
      </span>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">
          {t('rules.locationFraction.preferred')}
        </span>
        {renderPills(preferred, (v) => {
          // If lowering preferred (e.g. 1/4 → 1/8) the existing min may
          // become a *larger* share than preferred. Conversely, raising
          // preferred above min violates min ≤ preferred. Clear min when
          // the relationship would be inconsistent.
          if (v !== null && min !== null && min < v) {
            props.onUpdate({
              preferred_location_fraction_denominator: v,
              min_location_fraction_denominator: null,
            });
          } else {
            props.onUpdate({ preferred_location_fraction_denominator: v });
          }
        })}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">
          {t('rules.locationFraction.min')}
        </span>
        {renderPills(min, (v) => {
          // min may not be a *larger* share than preferred (smaller
          // denominator). If the user picks one that's stricter than
          // preferred, leave preferred alone and let the server's CHECK
          // catch it — but in practice we just won't let them: skip the
          // assignment when it would violate the envelope.
          if (v !== null && preferred !== null && v < preferred) return;
          props.onUpdate({ min_location_fraction_denominator: v });
        })}
      </div>
    </div>
  );
}

// ── WeekdayRow ──────────────────────────────────────────────────────
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function WeekdayRow(props: {
  value: Rules['allowed_weekdays'];
  onChange: (v: Rules['allowed_weekdays']) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('common');
  const arr: [boolean, boolean, boolean, boolean, boolean, boolean, boolean] =
    props.value ?? [true, true, true, true, true, true, true];
  return (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAY_KEYS.map((key, idx) => {
        const on = arr[idx];
        return (
          <button
            key={key}
            type="button"
            aria-pressed={on}
            disabled={props.disabled ?? false}
            onClick={() => {
              const next = [...arr] as [
                boolean,
                boolean,
                boolean,
                boolean,
                boolean,
                boolean,
                boolean,
              ];
              next[idx] = !on;
              props.onChange(next);
            }}
            className={cn(
              'rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
              on
                ? 'border-lime bg-lime text-ink'
                : 'border-border bg-background text-muted-foreground hover:bg-muted',
              props.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {t(`rules.weekdayShort.${key}`)}
          </button>
        );
      })}
    </div>
  );
}

// ── Section ─────────────────────────────────────────────────────────
function Section(props: {
  title: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3 sm:p-4">
      <legend className="px-1 font-heading text-sm font-semibold text-foreground">
        {props.title}
      </legend>
      {props.help && (
        <p className="text-xs text-muted-foreground">{props.help}</p>
      )}
      {props.children}
    </fieldset>
  );
}

// ── StrictnessLegend ────────────────────────────────────────────────
function StrictnessLegend() {
  const { t } = useTranslation('common');
  return (
    <aside className="flex flex-col gap-2 rounded-xl border border-border bg-accent-soft/60 p-3 text-xs">
      <span className="font-heading text-sm font-semibold text-foreground">
        {t('rules.legend.title')}
      </span>
      <ul className="flex flex-col gap-1 text-foreground">
        <li>
          <span className="font-semibold">{t('rules.strictness.must')}</span>
          <span> — {t('rules.legend.must')}</span>
        </li>
        <li>
          <span className="font-semibold">{t('rules.strictness.target')}</span>
          <span> — {t('rules.legend.target')}</span>
        </li>
        <li>
          <span className="font-semibold">{t('rules.strictness.prefer')}</span>
          <span> — {t('rules.legend.prefer')}</span>
        </li>
      </ul>
    </aside>
  );
}

// ── RulesForm ───────────────────────────────────────────────────────
export function RulesForm(props: RulesFormProps) {
  const {
    rules,
    onChange,
    inherited,
    inheritedLabel,
    locations,
    selectedLocationIDs,
    onSelectedLocationsChange,
    selectPortalContainer,
    disabled,
  } = props;
  const { t } = useTranslation('common');

  const update = (patch: Partial<Rules>) => onChange({ ...rules, ...patch });

  const ctx: CommonRowContext = {
    rules,
    inherited,
    inheritedLabel,
    onUpdate: update,
    disabled,
    selectPortalContainer,
  };

  return (
    <div className="flex flex-col gap-6">
      <StrictnessLegend />

      {/* Volume */}
      <Section title={t('rules.sections.volume')} help={t('rules.sections.volumeHelp')}>
        <EnvelopeRow
          {...ctx}
          label={t('rules.sessionsPerWeek')}
          help={t('rules.sessionsPerWeekHelp')}
          targetField="sessions_per_week_target"
          minField="sessions_per_week_min"
          maxField="sessions_per_week_max"
          strictnessField="sessions_per_week_strictness"
        />
      </Section>

      {/* Duration */}
      <Section title={t('rules.sections.duration')} help={t('rules.sections.durationHelp')}>
        <EnvelopeRow
          {...ctx}
          label={t('rules.sessionDuration')}
          help={t('rules.sessionDurationHelp')}
          targetField="session_duration_minutes_target"
          minField="session_duration_minutes_min"
          maxField="session_duration_minutes_max"
          strictnessField="session_duration_strictness"
          suffix={t('rules.suffixMinutes')}
        />
      </Section>

      {/* Time window */}
      <Section title={t('rules.sections.time')} help={t('rules.sections.timeHelp')}>
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <label className="text-sm font-medium text-foreground">
              {t('rules.timeWindow')}
            </label>
            <StrictnessSelect
              value={rules.time_window_strictness}
              onChangeValue={(s) => update({ time_window_strictness: s })}
              ariaLabel={t('rules.strictnessFor', { name: t('rules.timeWindow') })}
              disabled={disabled}
              selectPortalContainer={selectPortalContainer}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span>{t('rules.timeWindowStart')}</span>
              <TimePicker
                value={rules.time_window_start ?? ''}
                onChange={(v) => update({ time_window_start: v === '' ? null : v })}
                disabled={disabled ?? false}
                ariaLabel={t('rules.timeWindowStart')}
                selectPortalContainer={selectPortalContainer}
              />
            </div>
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span>{t('rules.timeWindowEnd')}</span>
              <TimePicker
                value={rules.time_window_end ?? ''}
                onChange={(v) => update({ time_window_end: v === '' ? null : v })}
                disabled={disabled ?? false}
                ariaLabel={t('rules.timeWindowEnd')}
                selectPortalContainer={selectPortalContainer}
              />
            </div>
          </div>
          {inherited?.time_window_start && inherited?.time_window_end && (
            <span className="text-xs text-muted-foreground">
              {t('rules.inheritedHint', {
                label: inheritedLabel ?? t('rules.inheritedDefault'),
                value: `${inherited.time_window_start}–${inherited.time_window_end}`,
              })}
            </span>
          )}
        </div>

        {/* Weekdays */}
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <label className="text-sm font-medium text-foreground">
              {t('rules.allowedWeekdays')}
            </label>
            <StrictnessSelect
              value={rules.allowed_weekdays_strictness}
              onChangeValue={(s) => update({ allowed_weekdays_strictness: s })}
              ariaLabel={t('rules.strictnessFor', {
                name: t('rules.allowedWeekdays'),
              })}
              disabled={disabled}
              selectPortalContainer={selectPortalContainer}
            />
          </div>
          <WeekdayRow
            value={rules.allowed_weekdays}
            onChange={(v) => update({ allowed_weekdays: v })}
            disabled={disabled ?? false}
          />
          <span className="text-xs text-muted-foreground">
            {t('rules.allowedWeekdaysHelp')}
          </span>
        </div>
      </Section>

      {/* Locations */}
      <Section title={t('rules.sections.locations')} help={t('rules.sections.locationsHelp')}>
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <label className="text-sm font-medium text-foreground">
            {t('rules.locationsStrictness')}
          </label>
          <StrictnessSelect
            value={rules.allowed_locations_strictness}
            onChangeValue={(s) => update({ allowed_locations_strictness: s })}
            ariaLabel={t('rules.strictnessFor', { name: t('rules.locationsStrictness') })}
            disabled={disabled}
            selectPortalContainer={selectPortalContainer}
          />
        </div>
        {locations.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('rules.locationsEmpty')}</p>
        ) : (
          <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-background p-2">
            {locations.map((loc) => {
              const checked = selectedLocationIDs.has(loc.id);
              return (
                <label
                  key={loc.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted"
                >
                  <Checkbox
                    checked={checked}
                    onChange={(e) => {
                      const next = new Set(selectedLocationIDs);
                      if (e.target.checked) next.add(loc.id);
                      else next.delete(loc.id);
                      onSelectedLocationsChange(next);
                    }}
                    disabled={disabled ?? false}
                  />
                  <span className="flex-1 truncate">{loc.name}</span>
                </label>
              );
            })}
          </div>
        )}

        <LocationFractionRow {...ctx} />
      </Section>

      {/* Personnel */}
      <Section title={t('rules.sections.personnel')} help={t('rules.sections.personnelHelp')}>
        <NumberRow
          {...ctx}
          label={t('rules.minLeadersPresent')}
          field="min_leaders_present"
          strictnessField="min_leaders_present_strictness"
          min={0}
          max={10}
          help={t('rules.minLeadersPresentHelp')}
        />
        <BooleanRow
          {...ctx}
          label={t('rules.headCoachRequired')}
          help={t('rules.headCoachRequiredHelp')}
          field="head_coach_required"
          strictnessField="head_coach_required_strictness"
        />
      </Section>
    </div>
  );
}
